import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import ProfileOverview from "../components/profile/ProfileOverview";
import ProfileSettings from "../components/profile/ProfileSettings";
import SpotSuggestionDraftPage from "./SpotSuggestionDraftPage";
import { openAdminPanel } from "../utils/adminPanel";
import { formatYmdHi } from "../utils/formatDateTime";
import {
    ProfileBlogCommentsList,
    ProfileCommentsList,
    ProfileListMeta,
    ProfileMySpotsList,
    ProfileSuggestionsList,
} from "../components/profile/ProfileTabContent";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const EMPTY_ARRAY = [];

const PROFILE_TABS = [
    { key: "", label: "Профиль" },
    { key: "my-spots", label: "Мои точки" },
    { key: "favorites", label: "Избранные точки" },
    { key: "likes", label: "Лайки" },
    { key: "comments", label: "Отчёты" },
    { key: "blog-comments", label: "Комментарии" },
    { key: "suggestions", label: "Предложения" },
    { key: "settings", label: "Настройки" }
];

const SPOT_STATUS_LABELS = {
    published: "Опубликовано",
    moderation_required: "На модерации",
    moderation: "На модерации",
    new: "На модерации",
    pending: "На модерации",
    rejected: "Отклонено",
    draft: "Черновик",
    discarded: "Отменено",
    approved: "Одобрено",
};
const COMMENT_STATUS_LABELS = {
    new: "На модерации",
    moderation_required: "На модерации",
    rejected: "Отклонено",
    published: "Опубликован",
};
const PAGE_SIZE = 20;

function toArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    return EMPTY_ARRAY;
}

function normalizeStatus(item) {
    if (item?.status) return String(item.status);
    if (item?.published === true) return "published";
    if (item?.published === false) return "moderation_required";
    return "";
}

function statusLabel(status) {
    return SPOT_STATUS_LABELS[status] || status || "Неизвестно";
}

function commentStatusLabel(raw) {
    const s = String(raw || "").trim();
    return COMMENT_STATUS_LABELS[s] || (s === "" ? "—" : s);
}

function suggestionStatus(item) {
    return item?.moderationStatus || item?.status || "";
}

async function fetchFirstArray(urls) {
    for (const url of urls) {
        try {
            const response = await axios.get(`${API_BASE_URL}${url}`, { withCredentials: true });
            const list = toArray(response.data);
            if (list.length || Array.isArray(response.data) || Array.isArray(response.data?.items)) {
                return list;
            }
        } catch {
            // try next endpoint
        }
    }
    return [];
}

export default function Profile() {
    const { user, ready, isAuthenticated, logout, refresh } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [spots, setSpots] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [likedSpots, setLikedSpots] = useState([]);
    const [comments, setComments] = useState([]);
    const [blogComments, setBlogComments] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [pageByTab, setPageByTab] = useState({
        "my-spots": 1,
        favorites: 1,
        likes: 1,
        comments: 1,
        "blog-comments": 1,
        suggestions: 1,
    });
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);

    const isAdmin = Boolean(user?.isAdmin);
    const tab = useMemo(() => {
        const slug = location.pathname.replace(/^\/profile\/?/, "");
        if (slug === "settings") return "settings";
        if (slug === "" || PROFILE_TABS.some((item) => item.key === slug)) {
            return slug;
        }
        if (/^suggestions\/\d+$/.test(slug)) {
            return "suggestions";
        }
        return "";
    }, [location.pathname]);
    const activeProfileTabLabel = tab === "settings"
        ? "Настройки профиля"
        : (PROFILE_TABS.find((item) => item.key === tab)?.label || "Профиль");

    const suggestionDraftId = useMemo(() => {
        const m = location.pathname.match(/^\/profile\/suggestions\/(\d+)$/);
        return m ? Number(m[1]) : null;
    }, [location.pathname]);


    useEffect(() => {
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        (async () => {
            const [spotsLoaded, commentsLoaded, blogCommentsLoaded, suggestionsLoaded, favoriteLinks, likesRaw] = await Promise.all([
                fetchFirstArray(["/api/spots/my"]),
                fetchFirstArray(["/api/comments/my"]),
                fetchFirstArray(["/api/blog-comments/my"]),
                fetchFirstArray(["/api/spots/drafts/my"]),
                fetchFirstArray(["/api/favorites"]),
                fetchFirstArray(["/api/likes/my"]),
            ]);

            const favoriteSpotIds = favoriteLinks
                .map((item) => Number(item?.spotId || item?.id))
                .filter((id) => Number.isFinite(id) && id > 0);
            const favoritesSettled = await Promise.allSettled(
                favoriteSpotIds.map((id) => axios.get(`${API_BASE_URL}/api/spots/${id}`, { withCredentials: true }))
            );
            const favoriteSpots = favoritesSettled
                .filter((item) => item.status === "fulfilled")
                .map((item) => item.value.data)
                .filter((item) => item?.id);

            const likeSpotIds = likesRaw
                .filter((item) => item?.entityType === "spot" || item?.type === "spot" || item?.spotId)
                .map((item) => Number(item?.entityId || item?.spotId))
                .filter((id) => Number.isFinite(id) && id > 0);
            const likesSettled = await Promise.allSettled(
                likeSpotIds.map((id) => axios.get(`${API_BASE_URL}/api/spots/${id}`, { withCredentials: true }))
            );
            const likesSpots = likesSettled
                .filter((item) => item.status === "fulfilled")
                .map((item) => item.value.data)
                .filter((item) => item?.id);

            if (!cancelled) {
                setSpots(spotsLoaded);
                setComments(commentsLoaded);
                setBlogComments(blogCommentsLoaded);
                setSuggestions(suggestionsLoaded);
                setFavorites(favoriteSpots);
                setLikedSpots(likesSpots);
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const stats = useMemo(() => {
        const pendingSpotStatuses = ["new", "draft", "moderation_required"];
        const spotModeration = spots.filter((item) => pendingSpotStatuses.includes(normalizeStatus(item))).length;
        const commentsModeration = comments.filter((item) => !(item.status === "published" || item.published === true)).length;
        const blogCommentsModeration = blogComments.filter((item) => !(item.status === "published" || item.published === true)).length;
        const suggestionsModeration = suggestions.filter((item) => suggestionStatus(item) === "moderation_required").length;
        return {
            commentsTotal: comments.length,
            blogCommentsTotal: blogComments.length,
            spotsTotal: spots.length,
            spotModeration,
            suggestionsModeration,
            commentsModeration,
            blogCommentsModeration,
            likesTotal: likedSpots.length,
            favoritesTotal: favorites.length,
        };
    }, [spots, comments, blogComments, suggestions, likedSpots, favorites]);

    const renderProfileMenu = () => (
        <aside className={`profile-glass-menu${profileMenuOpen ? " is-open" : ""}`}>
            <button
                type="button"
                className="profile-glass-menu__summary"
                onClick={() => setProfileMenuOpen((value) => !value)}
                aria-expanded={profileMenuOpen}
            >
                <span>{activeProfileTabLabel}</span>
                <svg aria-hidden className="profile-glass-menu__chevron" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            <div className="profile-glass-menu__body">
                {PROFILE_TABS.map((item) => (
                    <NavLink
                        key={item.key || "profile"}
                        to={item.key ? `/profile/${item.key}` : "/profile"}
                        end={item.key !== "suggestions"}
                        className={({ isActive }) => `profile-glass-menu__link${isActive ? " is-active" : ""}`}
                        onClick={() => setProfileMenuOpen(false)}
                    >
                        {item.label}
                    </NavLink>
                ))}
                {isAdmin && (
                    <button type="button" className="profile-glass-menu__link profile-glass-menu__button" onClick={openAdminPanel}>
                        Админка
                    </button>
                )}
                <button
                    type="button"
                    className="profile-glass-menu__link profile-glass-menu__button profile-glass-menu__button--logout"
                    onClick={() => {
                        logout();
                        navigate("/map");
                    }}
                >
                    Выйти
                </button>
            </div>
        </aside>
    );

    const paginate = (items, tabKey) => {
        const currentPage = pageByTab[tabKey] || 1;
        const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
        const safePage = Math.min(currentPage, totalPages);
        const start = (safePage - 1) * PAGE_SIZE;
        const pageItems = items.slice(start, start + PAGE_SIZE);
        return { currentPage: safePage, totalPages, pageItems };
    };

    const setPage = (tabKey, page) => {
        setPageByTab((prev) => ({ ...prev, [tabKey]: page }));
    };

    const renderPaginatedList = (tabKey, items, emptyText, renderItems) => {
        const { pageItems, currentPage, totalPages } = paginate(items, tabKey);
        return (
            <>
                <ProfileListMeta
                    total={items.length}
                    currentCount={pageItems.length}
                    tabKey={tabKey}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    pageSize={PAGE_SIZE}
                />
                {!items.length ? <p className="page-status-message">{emptyText}</p> : renderItems(pageItems)}
            </>
        );
    };

    const renderContent = () => {
        if (loading) return <p className="page-status-message">Загрузка...</p>;

        if (tab === "my-spots") {
            return renderPaginatedList("my-spots", spots, "Точек пока нет.", (pageItems) => (
                <ProfileMySpotsList
                    spots={pageItems}
                    statusLabel={statusLabel}
                    normalizeStatus={normalizeStatus}
                />
            ));
        }
        if (tab === "favorites") {
            return renderPaginatedList("favorites", favorites, "Избранных точек пока нет.", (pageItems) => (
                <ProfileMySpotsList spots={pageItems} showStats={false} />
            ));
        }
        if (tab === "likes") {
            return renderPaginatedList("likes", likedSpots, "Лайкнутых точек пока нет.", (pageItems) => (
                <ProfileMySpotsList spots={pageItems} showStats={false} />
            ));
        }
        if (tab === "comments") {
            return renderPaginatedList("comments", comments, "Отчётов пока нет.", (pageItems) => (
                <ProfileCommentsList
                    comments={pageItems}
                    commentStatusLabel={commentStatusLabel}
                    formatDate={(value) => formatYmdHi(value) || "-"}
                />
            ));
        }
        if (tab === "blog-comments") {
            return renderPaginatedList("blog-comments", blogComments, "Комментариев пока нет.", (pageItems) => (
                <ProfileBlogCommentsList
                    comments={pageItems}
                    commentStatusLabel={commentStatusLabel}
                    formatDate={(value) => formatYmdHi(value) || "-"}
                />
            ));
        }
        if (tab === "settings") {
            return <ProfileSettings user={user} onUpdated={() => refresh()} />;
        }
        if (tab === "suggestions") {
            return renderPaginatedList("suggestions", suggestions, "Предложений пока нет.", (pageItems) => (
                <ProfileSuggestionsList
                    suggestions={pageItems}
                    statusLabel={statusLabel}
                    suggestionStatus={suggestionStatus}
                    formatDate={(value) => formatYmdHi(value) || "-"}
                />
            ));
        }

        return (
            <ProfileOverview
                user={user}
                stats={stats}
                spots={spots}
                comments={comments}
                blogComments={blogComments}
                suggestions={suggestions}
                favorites={favorites}
            />
        );
    };

    if (!ready) {
        return (
            <div className="blog-page-root">
                <div className="blog-page-inner profile-glass-inner">
                    <div className="cms-page-panel page-auth-panel"><p className="cms-page-loading">Загрузка...</p></div>
                </div>
            </div>
        );
    }
    if (!isAuthenticated) {
        return (
            <div className="blog-page-root">
                <div className="blog-page-inner profile-glass-inner">
                    <div className="cms-page-panel page-auth-panel">
                        <h1 className="cms-page-title">Профиль</h1>
                        <p className="page-status-message">Нужно войти в аккаунт.</p>
                        <button type="button" className="toolbar-btn" onClick={() => navigate("/login")}>Войти</button>
                    </div>
                </div>
            </div>
        );
    }

    if (suggestionDraftId) {
        return (
            <div className="blog-page-root">
                <div className="blog-page-inner profile-glass-inner">
                    <div className="profile-glass-layout">
                        {renderProfileMenu()}
                        <div className="cms-page-panel">
                            <SpotSuggestionDraftPage draftId={suggestionDraftId} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-page-root">
            <div className="blog-page-inner profile-glass-inner">
                <div className="profile-glass-layout">
                    {renderProfileMenu()}
                    <div className={`cms-page-panel profile-glass-content${tab === "" ? " profile-glass-content--overview" : ""}`}>
                        {tab !== "" && <h1 className="page-title">{activeProfileTabLabel}</h1>}
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
}
